/**
 * INYRON Life Causality Graph
 *
 * Shared, deterministic history layer for living entities. It records causal
 * events and relationships without replacing LCS, CreatureManager, NPC world
 * authorities, or the UniversalLifeRuntime simulation loop.
 */
export type LifeRelation = "trust" | "fear" | "affection" | "rivalry" | "kinship" | "debt" | "leadership" | "territory";
export type LifeCauseKind = "birth" | "observation" | "decision" | "interaction" | "combat" | "rescue" | "death" | "discovery" | "migration" | "environment";
export interface LifeCause { id: string; at: number; kind: LifeCauseKind; actorId?: string; targetId?: string; placeId?: string; summary: string; salience: number; parentIds: string[]; }
export interface LifeRelationship { sourceId: string; targetId: string; relation: LifeRelation; value: number; updatedAt: number; causeIds: string[]; }
export interface LifeCausalitySnapshot { version: 1; causes: LifeCause[]; relationships: LifeRelationship[] }

const RELATIONS = new Set<LifeRelation>(["trust", "fear", "affection", "rivalry", "kinship", "debt", "leadership", "territory"]);
const CAUSE_KINDS = new Set<LifeCauseKind>(["birth", "observation", "decision", "interaction", "combat", "rescue", "death", "discovery", "migration", "environment"]);
const clamp = (v: number) => Math.max(-1, Math.min(1, v));
const stableId = (kind: string, at: number, actorId = "", targetId = "", summary = "") => `${kind}:${at}:${actorId}:${targetId}:${summary}`.replace(/\s+/g, "_").slice(0, 180);
const finite = (v: unknown, fallback = 0) => typeof v === "number" && Number.isFinite(v) ? v : fallback;
const nonEmpty = (v: unknown): v is string => typeof v === "string" && v.length > 0;
const cloneCause = (cause: LifeCause): LifeCause => ({ ...cause, parentIds: [...cause.parentIds] });
const cloneRelationship = (relationship: LifeRelationship): LifeRelationship => ({ ...relationship, causeIds: [...relationship.causeIds] });

export class LifeCausalityGraph {
  private causes = new Map<string, LifeCause>();
  private relations = new Map<string, LifeRelationship>();
  private recentByActor = new Map<string, string[]>();

  record(input: Omit<LifeCause, "id" | "parentIds"> & { id?: string; parentIds?: string[] }) {
    const id = input.id ?? stableId(input.kind, input.at, input.actorId, input.targetId, input.summary);
    if (this.causes.has(id)) return this.causes.get(id)!;
    const parents = input.parentIds ?? (input.actorId ? (this.recentByActor.get(input.actorId) ?? []).slice(-4) : []);
    const cause: LifeCause = { ...input, id, parentIds: [...parents], salience: Math.max(0, Math.min(1, input.salience)) };
    this.causes.set(id, cause);
    if (input.actorId) this.appendRecent(input.actorId, id);
    return cause;
  }

  relate(sourceId: string, targetId: string, relation: LifeRelation, value: number, at: number, causeId?: string) {
    const key = `${sourceId}:${targetId}:${relation}`;
    const current = this.relations.get(key);
    const causeIds = [...(current?.causeIds ?? [])];
    if (causeId && !causeIds.includes(causeId)) causeIds.push(causeId);
    const next: LifeRelationship = { sourceId, targetId, relation, value: clamp(value), updatedAt: at, causeIds: causeIds.slice(-16) };
    this.relations.set(key, next); return next;
  }

  /** Erase only the provisional identity's records after an aborted birth. */
  rollbackUncommittedEntity(entityId: string): void {
    if (!nonEmpty(entityId)) return;
    const removed = new Set<string>();
    for (const [id, cause] of this.causes) {
      if (cause.actorId === entityId || cause.targetId === entityId) {
        this.causes.delete(id);
        removed.add(id);
      }
    }
    for (const [id, cause] of this.causes) {
      if (cause.parentIds.some(parent => removed.has(parent))) {
        this.causes.set(id, { ...cause, parentIds: cause.parentIds.filter(parent => !removed.has(parent)) });
      }
    }
    for (const [key, relation] of this.relations) {
      if (relation.sourceId === entityId || relation.targetId === entityId) this.relations.delete(key);
      else if (relation.causeIds.some(id => removed.has(id))) {
        this.relations.set(key, { ...relation, causeIds: relation.causeIds.filter(id => !removed.has(id)) });
      }
    }
    for (const [actor, ids] of this.recentByActor) {
      const next = ids.filter(id => !removed.has(id));
      if (next.length) this.recentByActor.set(actor, next);
      else this.recentByActor.delete(actor);
    }
  }

  getCause(id: string) { return this.causes.get(id); }
  getRelationship(sourceId: string, targetId: string, relation?: LifeRelation) {
    if (relation) return this.relations.get(`${sourceId}:${targetId}:${relation}`);
    return [...this.relations.values()].filter(r => r.sourceId === sourceId && r.targetId === targetId);
  }
  historyFor(actorId: string) { return [...this.causes.values()].filter(c => c.actorId === actorId || c.targetId === actorId).sort((a,b) => a.at-b.at); }
  recentFor(actorId: string) { return (this.recentByActor.get(actorId) ?? []).map(id => this.causes.get(id)).filter(Boolean) as LifeCause[]; }
  snapshot(): LifeCausalitySnapshot { return { version: 1, causes: [...this.causes.values()].map(cloneCause), relationships: [...this.relations.values()].map(cloneRelationship) }; }
  restore(snapshot: unknown): { causes: number; relationships: number } {
    if (!snapshot || typeof snapshot !== "object") return { causes: 0, relationships: 0 };
    const candidate = snapshot as Partial<LifeCausalitySnapshot>;
    if (candidate.version !== 1 || !Array.isArray(candidate.causes) || !Array.isArray(candidate.relationships)) return { causes: 0, relationships: 0 };

    const causes: LifeCause[] = [];
    for (const raw of candidate.causes) {
      if (!raw || typeof raw !== "object") continue;
      const cause = raw as Partial<LifeCause>;
      if (!nonEmpty(cause.id) || !CAUSE_KINDS.has(cause.kind as LifeCauseKind) || !nonEmpty(cause.summary)) continue;
      causes.push({
        id: cause.id,
        at: Math.max(0, finite(cause.at)),
        kind: cause.kind as LifeCauseKind,
        ...(nonEmpty(cause.actorId) ? { actorId: cause.actorId } : {}),
        ...(nonEmpty(cause.targetId) ? { targetId: cause.targetId } : {}),
        ...(nonEmpty(cause.placeId) ? { placeId: cause.placeId } : {}),
        summary: cause.summary,
        salience: Math.max(0, Math.min(1, finite(cause.salience))),
        parentIds: Array.isArray(cause.parentIds) ? cause.parentIds.filter(nonEmpty).slice(-16) : [],
      });
    }

    this.clear();
    causes.sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
    for (const cause of causes) {
      this.causes.set(cause.id, cause);
      if (cause.actorId) this.appendRecent(cause.actorId, cause.id);
    }

    let relationships = 0;
    for (const raw of candidate.relationships) {
      if (!raw || typeof raw !== "object") continue;
      const relation = raw as Partial<LifeRelationship>;
      if (!nonEmpty(relation.sourceId) || !nonEmpty(relation.targetId) || !RELATIONS.has(relation.relation as LifeRelation)) continue;
      const restored: LifeRelationship = {
        sourceId: relation.sourceId,
        targetId: relation.targetId,
        relation: relation.relation as LifeRelation,
        value: clamp(finite(relation.value)),
        updatedAt: Math.max(0, finite(relation.updatedAt)),
        causeIds: Array.isArray(relation.causeIds) ? relation.causeIds.filter(nonEmpty).filter(id => this.causes.has(id)).slice(-16) : [],
      };
      this.relations.set(`${restored.sourceId}:${restored.targetId}:${restored.relation}`, restored);
      relationships += 1;
    }
    return { causes: causes.length, relationships };
  }
  clear() { this.causes.clear(); this.relations.clear(); this.recentByActor.clear(); }

  private appendRecent(actorId: string, causeId: string) {
    const list = this.recentByActor.get(actorId) ?? [];
    list.push(causeId);
    if (list.length > 32) list.splice(0, list.length - 32);
    this.recentByActor.set(actorId, list);
  }
}

export const lifeCausalityGraph = new LifeCausalityGraph();
